// frontend/src/pages/public/Privacy/PrivacyContent.ts

export const privacyPolicyPage = {
  eyebrow: "Privacy",
  title: "Privacy Policy",
  subtitle:
    "AcceptingMD is committed to protecting your privacy. This page explains how we collect, use, and safeguard information on the platform.",
  sections: [
    {
      title: "Overview",
      body: [
        'AcceptingMD (“we”, “our”, “us”) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, retain, and safeguard information when you use our platform.',
        "AcceptingMD is a care discovery and connection platform that supports family doctor intake and attachment-related request workflows. We do not provide medical care, make clinical decisions, or make clinic acceptance or attachment decisions. Clinics and providers independently manage their own intake processes, clinical records, and patient relationships.",
      ],
    },
    {
      title: "Information We Collect",
      body: [
        "Depending on how you use the platform, we may collect information you provide directly, including contact details such as name, email address, and phone number; location information such as city or postal code; and preferences related to care such as language, accessibility needs, clinic hours, or provider preferences.",
        "If you submit a patient request form to connect with a family doctor, we collect the information you provide for that request, including information needed to process, verify, route, manage, and support the request.",
        "AcceptingMD does not collect medical records, clinical documentation, lab results, diagnostic reports, or detailed medical histories through the public patient request form.",
      ],
    },
    {
      title: "Information Shared With Clinics",
      body: [
        "When you submit a request to become a patient, the information you provide is securely shared with the selected clinic or provider so they can review your request and contact you about next steps.",
        "Clinics independently determine what information they require, decide whether to accept, decline, waitlist, invite, or otherwise respond to requests, and contact patients directly regarding next steps.",
        "AcceptingMD does not control clinic intake decisions, clinical decisions, attachment decisions, appointment availability, or the clinic’s own medical record practices.",
        "If a clinic copies information from AcceptingMD into its own EMR, files, or clinic systems, that clinic is responsible for the retention, access, correction, and privacy handling of those clinic-held records.",
      ],
    },
    {
      title: "Automatically Collected Information",
      body: [
        "We may automatically collect limited technical information such as device type and browser, IP address used for security and abuse prevention, and basic usage analytics such as pages visited and interactions.",
        "This information is used to improve platform performance, reliability, troubleshooting, fraud prevention, security monitoring, and abuse prevention.",
      ],
    },
    {
      title: "Cookies & Similar Technologies",
      body: [
        "AcceptingMD uses essential cookies and similar local storage technologies to operate the website, keep it secure, support authentication where applicable, and remember basic preferences such as whether you have dismissed notices.",
        "We do not use advertising cookies and we do not sell personal information.",
      ],
    },
    {
      title: "How We Use Information",
      body: [
        "We use information collected to help patients find and connect with appropriate care providers, process submitted requests, deliver requests to selected clinics, verify request-related email workflows, send system notifications such as request status emails, support clinic and provider workflows, improve platform functionality and user experience, maintain auditability, and protect the security and integrity of the platform.",
        "We may also use information to respond to support requests, privacy requests, correction requests, deletion or retention review requests, privacy complaints, security concerns, or legal and operational requirements.",
        "We do not sell personal information.",
      ],
    },
    {
      title: "Health Information & PHIPA",
      body: [
        "AcceptingMD is designed to minimize the collection of personal health information.",
        "We do not store medical histories, diagnoses, lab results, diagnostic reports, or clinical notes through the public patient request form. Any detailed health-related information requested by clinics is managed directly by those clinics through their own processes.",
        "Participating clinics and providers are responsible for their own compliance with PHIPA and other applicable health privacy laws for records under their custody or control.",
        "AcceptingMD supports the request workflow as a technology platform. It is not an EMR, medical record system, diagnostic tool, emergency service, or clinical care provider.",
      ],
    },
    {
      title: "Disclosure of Information",
      body: [
        "We may disclose information to clinics or providers you choose to contact, to service providers supporting platform operations such as hosting, email delivery, monitoring, mapping, security, and support, or when required by law or legal process.",
        "Service providers supporting platform operations are expected to handle information only for approved platform purposes and under appropriate confidentiality, security, and privacy obligations.",
        "We do not disclose personal information for marketing purposes without consent.",
      ],
    },
    {
      title: "Data Security",
      body: [
        "We use administrative, technical, and physical safeguards to protect information, including secure hosting environments, encrypted data transmission, access controls, monitoring, and role-based access restrictions.",
        "Access to request information is intended to be limited to authorized users based on their role and clinic, provider, navigator, or OHT scope where applicable.",
        "While no system can guarantee absolute security, we take reasonable steps to protect your information and to investigate suspected privacy or security incidents.",
      ],
    },
    {
      title: "Data Retention",
      body: [
        "We retain request information only as long as needed for the request workflow, clinic follow-up, support, auditability, privacy and security purposes, legal or operational requirements, and platform accountability.",
        "A request may expire after the clinic response period or may be cancelled by the patient, but expiry or cancellation does not always mean the underlying record is immediately deleted.",
        "Expired, cancelled, declined, waitlisted, invited, completed, or otherwise closed request records may be retained for a defined period to support audit, privacy review, security investigation, duplicate prevention, clinic accountability, support, legal requirements, and operational review.",
        "We may retain limited audit logs, privacy request records, complaint records, incident records, email delivery metadata, and technical logs for longer periods where needed for security, accountability, legal, or operational reasons.",
        "We may retain non-identifying aggregate information for planning, reporting, system improvement, and health system coordination, provided it is handled in a way intended to reduce the risk of identifying individuals.",
      ],
    },
    {
      title: "Access, Correction, and Deletion Review",
      body: [
        "You may contact us to request access to or correction of personal information held in the AcceptingMD platform, subject to identity verification and applicable legal, privacy, security, and operational requirements.",
        "You may also request deletion or retention review of information held in the AcceptingMD platform. Deletion may not always be possible where information must be retained for audit, privacy, security, legal, clinic, backup, or operational reasons.",
        "If your information has already been shared with or copied into a clinic’s EMR, files, or clinic systems, access, correction, or deletion requests related to those clinic-held records should be directed to the clinic.",
        "AcceptingMD does not independently alter clinic EMR records, clinical records, or clinic-controlled medical records.",
      ],
    },
    {
      title: "Privacy Complaints",
      body: [
        "If you have a privacy concern about how AcceptingMD collects, uses, discloses, safeguards, retains, or handles information through the platform, you may contact us using the contact information below.",
        "If your concern relates to a clinic’s clinical care, appointment decision, acceptance or decline decision, clinic communication, EMR record, or clinic privacy practice, the concern should generally be directed to the clinic.",
        "If a complaint suggests a possible privacy breach, unauthorized access, or security incident involving the AcceptingMD platform, we will review and escalate it through our privacy and security review process.",
      ],
    },
    {
      title: "Your Choices & Rights",
      body: [
        "You may cancel a submitted request using the provided status or manage-request link where that workflow is available.",
        "You may choose which listed clinic or provider to contact through the platform, subject to availability and the clinic’s own intake rules.",
        "You may contact us to ask questions about platform-held information, request access or correction, request deletion or retention review, or raise a privacy concern, subject to verification and applicable requirements.",
      ],
    },
    {
      title: "Third-Party Websites",
      body: [
        "AcceptingMD may link to third-party websites such as clinic websites, provider websites, maps, or other external resources. We are not responsible for the privacy practices of external sites.",
      ],
    },
    {
      title: "Changes to This Policy",
      body: [
        "We may update this Privacy Policy periodically. Updates will be posted on this page with a revised “Last updated” date where applicable.",
        "Continued use of the platform after an update indicates acceptance of the updated policy.",
      ],
    },
    {
      title: "Contact Us",
      body: [
        "If you have questions or concerns about this Privacy Policy or how your information is handled by AcceptingMD, please contact:",
        "AcceptingMD",
        "Email: support@acceptingmd.ca",
      ],
    },
  ],
  ctas: {
    title: "Need help?",
    subtitle:
      "You can learn how the platform works or return to care search at any time.",
    primary: { label: "How It Works", to: "/public/how-it-works" },
    secondary: { label: "Find a Family Doctor", to: "/public/find-family-doctor" },
  },
};